import { useMemo, useState } from 'react';
import { Button, Table, Dialog, ConfirmDialog, Input, Select, Switch, Badge, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Plus, Pencil, Trash2, Ban } from 'lucide-react';
import { PageHeader, QueryState, EmptyState, SectionCard } from '@/components';
import { errorText, formatINR } from '@/lib/format';
import { useQuery } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import Can from '@rbac/Can';
import { useCrudList, useCrudWrite } from '../hooks';
import { emptyOfferForm, validateOfferForm, offerPayload, resellerRange } from '../lib/supply';

/**
 * Supply (B16-V3, vendor stores only, D57): offer listings to dropshipping
 * stores at a wholesale price, with an optional maximum margin they may add
 * (D54). Every dropshipper sees enabled offers; block the ones you do not want
 * reselling your products (D68). Resellers never see your selling price or cost,
 * and you never see theirs.
 */
export default function Supply() {
  const can = useCan();
  const { toast } = useToast();
  const offers = useCrudList('SupplyOffer', { orderBy: { column: 'id', direction: 'desc' }, limit: 1000 });
  const listings = useCrudList('Listing', { select: ['id', 'title', 'price_paise', 'status'], orderBy: { column: 'id', direction: 'desc' }, limit: 2000 });
  const blocks = useQuery('crud', { model: 'SupplyOffer', action: 'blocks' }, { select: (r) => r.data || [] });
  const write = useCrudWrite();
  const [editing, setEditing] = useState(null); // { id|null, form }
  const [removing, setRemoving] = useState(null);
  const [block, setBlock] = useState({ store: '', reason: '' });
  const [unblocking, setUnblocking] = useState(null);
  const [formError, setFormError] = useState('');

  const byId = useMemo(() => new Map((listings.data || []).map((l) => [String(l.id), l])), [listings.data]);
  const offered = useMemo(() => new Set((offers.data || []).map((o) => String(o.listing_id))), [offers.data]);
  const listingOf = (o) => byId.get(String(o.listing_id)) || {};
  const form = editing && editing.form;
  const picked = form ? byId.get(String(form.listing_id)) : null;
  const errors = form ? validateOfferForm(form, picked ? picked.price_paise : null) : {};
  const setForm = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  const run = async (fn, done) => {
    setFormError('');
    try {
      await fn();
      done();
      offers.reload();
    } catch (ex) {
      setFormError(errorText(ex));
    }
  };
  const save = () => {
    const payload = offerPayload(form);
    if (editing.id) {
      const { listing_id, ...terms } = payload;
      return run(() => write.update('SupplyOffer', editing.id, terms), () => { toast.success('Offer saved', picked ? picked.title : ''); setEditing(null); });
    }
    return run(() => write.create('SupplyOffer', payload), () => { toast.success('Offer added', picked ? picked.title : ''); setEditing(null); });
  };
  const toggle = (o, on) => run(() => write.update('SupplyOffer', o.id, { dropship_enabled: on ? 1 : 0 }), () => toast.success(on ? 'Open to resellers' : 'Closed to resellers', listingOf(o).title || ''));
  const remove = () => run(() => write.run({ model: 'SupplyOffer', action: 'delete', where: { id: removing.id } }), () => { toast.success('Offer removed', listingOf(removing).title || ''); setRemoving(null); });
  const addBlock = async () => {
    setFormError('');
    try {
      const r = await write.run({ model: 'SupplyOffer', action: 'block', data: block });
      toast.success('Store blocked', (r && r.data && r.data.store) || block.store);
      setBlock({ store: '', reason: '' });
      blocks.reload();
    } catch (ex) {
      setFormError(errorText(ex));
    }
  };
  const removeBlock = async () => {
    try {
      await write.run({ model: 'SupplyOffer', action: 'unblock', data: { store: unblocking.store } });
      toast.success('Store unblocked', unblocking.name);
      setUnblocking(null);
      blocks.reload();
    } catch (ex) {
      setFormError(errorText(ex));
    }
  };

  const listingOptions = (listings.data || [])
    .filter((l) => (editing && editing.id ? String(l.id) === String(form.listing_id) : !offered.has(String(l.id))))
    .map((l) => ({ value: String(l.id), label: `${l.title} · ${formatINR(l.price_paise)}${l.status === 'published' ? '' : ` (${l.status})`}` }));

  const actions = [
    can('supply', 'update') && { name: 'edit', label: 'Edit', icon: <Pencil size={14} />, inline: true, onClick: ({ row }) => { setFormError(''); setEditing({ id: row.id, form: emptyOfferForm(row) }); } },
    can('supply', 'delete') && { name: 'remove', label: 'Remove', icon: <Trash2 size={14} />, onClick: ({ row }) => { setFormError(''); setRemoving(row); } },
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Supply"
        subtitle="Offer your products to reseller stores. They pay your wholesale price and add their margin, up to the cap you set; you ship under their name."
        actions={<Can module="supply" action="create"><Button leftIcon={<Plus size={16} />} onClick={() => { setFormError(''); setEditing({ id: null, form: emptyOfferForm() }); }}>Offer a product</Button></Can>}
      />
      {formError && !editing && !removing && !unblocking ? <Alert variant="error" title="Not saved" dismissible onDismiss={() => setFormError('')}>{formError}</Alert> : null}

      <QueryState query={offers}>
        {(rows) =>
          rows.length ? (
            <Table
              data={rows}
              columns={[
                { key: 'listing', label: 'Product', render: (o) => <span className="font-medium">{listingOf(o).title || `Listing ${o.listing_id}`}</span> },
                { key: 'price', label: 'Your price', render: (o) => formatINR(listingOf(o).price_paise), hideOnMobile: true },
                { key: 'wholesale_paise', label: 'Wholesale', render: (o) => formatINR(o.wholesale_paise) },
                { key: 'cap', label: 'Resellers charge', render: (o) => resellerRange(o.wholesale_paise, o.max_margin_pct, (p) => formatINR(p)), hideOnMobile: true },
                { key: 'handling_days', label: 'Ships in', render: (o) => `${o.handling_days} d`, hideOnMobile: true },
                {
                  key: 'dropship_enabled', label: 'Open',
                  render: (o) => <Switch checked={Number(o.dropship_enabled) === 1} onChange={(on) => toggle(o, on)} ariaLabel={`Offer ${listingOf(o).title || o.listing_id} to resellers`} disabled={!can('supply', 'update')} size="sm" />,
                },
              ]}
              actions={actions}
              replaceDefaultActions
              withAction={actions.length > 0}
              showSerial={false}
              showColumnPicker={false}
              globalSearch
              pagination
              pageSize={25}
            />
          ) : (
            <EmptyState title="No supply offers yet">Offer a listed product to reseller stores at a wholesale price.</EmptyState>
          )
        }
      </QueryState>

      <SectionCard title="Blocked reseller stores">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Every reseller store can import your open offers. Block a store by its web name to stop it reselling your products; a block on a store that does not resell has no effect.</p>
          <Can module="supply" action="update">
            <form className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]" onSubmit={(e) => { e.preventDefault(); if (block.store.trim()) addBlock(); }}>
              <Input aria-label="Store web name" placeholder="Store web name, e.g. resellr" value={block.store} onChange={(e) => setBlock({ ...block, store: e.target.value })} maxLength={64} />
              <Input aria-label="Reason" placeholder="Reason (optional)" value={block.reason} onChange={(e) => setBlock({ ...block, reason: e.target.value })} maxLength={255} />
              <Button type="submit" variant="outline" leftIcon={<Ban size={16} />} disabled={!block.store.trim()} loading={write.loading && !editing && !removing && !unblocking}>Block</Button>
            </form>
          </Can>
          <QueryState query={blocks}>
            {(rows) => (rows.length ? (
              <ul className="divide-y divide-[var(--app-border)]">
                {rows.map((b) => (
                  <li key={b.store} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <span><span className="font-medium">{b.name}</span> <code className="text-xs opacity-70">{b.store}</code>{b.reason ? <span className="block text-xs opacity-70">{b.reason}</span> : null}</span>
                    {can('supply', 'update') ? <Button size="sm" variant="ghost" onClick={() => setUnblocking(b)}>Unblock</Button> : <Badge variant="secondary" size="sm">Blocked</Badge>}
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm opacity-60">No stores blocked.</p>)}
          </QueryState>
        </div>
      </SectionCard>

      <Dialog isOpen={!!editing} onClose={() => setEditing(null)} title={editing && editing.id ? 'Edit supply offer' : 'Offer a product to resellers'} size="md">
        {form ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (!Object.keys(errors).length) save(); }}>
            <Select
              label="Listing"
              options={listingOptions}
              value={form.listing_id}
              onChange={(v) => setForm('listing_id', v || '')}
              searchable
              closeOnSelect
              disabled={Boolean(editing.id)}
              error={form.listing_id ? undefined : errors.listing_id}
              helperText={picked ? `Your price: ${formatINR(picked.price_paise)}. Only listings on sale reach resellers.` : undefined}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Wholesale price (₹)" inputMode="decimal" value={form.wholesale} onChange={(e) => setForm('wholesale', e.target.value)} error={form.wholesale ? errors.wholesale : undefined} required />
              <Input label="Maximum reseller margin (%)" inputMode="decimal" value={form.cap} onChange={(e) => setForm('cap', e.target.value)} error={errors.cap} helperText="Leave empty to let resellers set any price above wholesale." />
              <Input label="Ships within (days)" inputMode="numeric" value={form.handling_days} onChange={(e) => setForm('handling_days', e.target.value)} error={errors.handling_days} />
              <div className="flex items-end pb-2"><Switch checked={form.enabled} onChange={(on) => setForm('enabled', Boolean(on))} rightLabel="Open to resellers" ariaLabel="Open to resellers" /></div>
            </div>
            {!errors.wholesale && !errors.cap && form.wholesale ? (
              <p className="text-sm opacity-80">Resellers can charge {resellerRange(offerPayload(form).wholesale_paise, form.cap, (p) => formatINR(p))}.</p>
            ) : null}
            {formError ? <Alert variant="error" title="Not saved">{formError}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" loading={write.loading} disabled={Object.keys(errors).length > 0}>Save</Button>
            </div>
          </form>
        ) : null}
      </Dialog>

      <ConfirmDialog
        isOpen={!!removing}
        title="Remove this supply offer?"
        description="Resellers can no longer import it. Their existing imports stop selling at the next catalog update."
        confirmLabel="Remove"
        loading={write.loading}
        onCancel={() => setRemoving(null)}
        onConfirm={remove}
      />
      <ConfirmDialog
        isOpen={!!unblocking}
        title={unblocking ? `Unblock ${unblocking.name}?` : 'Unblock?'}
        description="The store can import your open offers again."
        confirmLabel="Unblock"
        loading={write.loading}
        onCancel={() => setUnblocking(null)}
        onConfirm={removeBlock}
      />
    </div>
  );
}
